import { type DesktopBreakpoint, useIsDesktop } from "@src/hooks/useMediaQuery"
import { type ReactNode, useEffect } from "react"
import { Dialog, type DialogProps } from "./Dialog"
import Drawer, { type DrawerProps } from "./Drawer"

type DialogVariant = {
  type: "dialog"
  props?: Omit<DialogProps, "children" | "onClose" | "open">
}

type DrawerVariant = {
  type: "drawer"
  props?: Omit<DrawerProps, "children" | "onClose" | "open">
}

type CustomVariant = {
  type: "custom"
  render: (props: {
    open: boolean
    onClose: () => void
    onCloseAnimationEnd?: () => void
    children: ReactNode
  }) => ReactNode
}

export type AdaptiveOverlayVariant =
  | DialogVariant
  | DrawerVariant
  | CustomVariant
  | null

export type AdaptiveOverlayProps = {
  open: boolean
  onClose: () => void
  onCloseAnimationEnd?: () => void
  desktopBreakpoint?: DesktopBreakpoint
  mobile: AdaptiveOverlayVariant
  desktop: AdaptiveOverlayVariant
  children: ReactNode
}

export function AdaptiveOverlay({
  open,
  onClose,
  onCloseAnimationEnd,
  desktopBreakpoint,
  mobile,
  desktop,
  children,
}: AdaptiveOverlayProps) {
  const isDesktop = useIsDesktop({ breakpoint: desktopBreakpoint })
  const variant = isDesktop ? desktop : mobile
  const hasVariant = variant != null

  useEffect(() => {
    if (open && !hasVariant) {
      onClose()
    }
  }, [hasVariant, onClose, open])

  if (variant == null) {
    return null
  }

  if (variant.type === "custom") {
    return variant.render({ open, onClose, onCloseAnimationEnd, children })
  }

  if (variant.type === "dialog") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onCloseAnimationEnd={onCloseAnimationEnd}
        {...variant.props}
      >
        {children}
      </Dialog>
    )
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onCloseAnimationEnd={onCloseAnimationEnd}
      {...variant.props}
    >
      {children}
    </Drawer>
  )
}
